import React, { useState, useCallback, useEffect } from 'react';
import { Spin, message, Modal, Input, Select, Divider, Row, Col, Popover } from 'antd';
import reportService from '../services/reportService';
import apiClient from '../services/apiClient';
import settingsService from '../services/settingsService';

// ─── Brand colours (same as MultiCampaignReport) ─────────────────────────────
const C = {
  teal:       '#267a96',
  tealLight:  '#E6F6F8',
  navy:       '#1A3A6B',
  blue:       '#267a96',
  blueMid:    '#1A3A6B',
  orange:     '#D95E1E',
  red:        '#D94040',
  redLight:   '#FDEAEA',
  amber:      '#D97706',
  amberLight: '#FEF3DC',
  green:      '#16A34A',
  greenLight: '#E8F7EE',
  border:     '#E5E7EB',
  text:       '#111827',
  textMid:    '#374151',
  textLight:  '#6B7280',
  bg:         '#F9FAFB',
  white:      '#FFFFFF',
};

const pct = (v, d = 1) => `${Number(v || 0).toFixed(d)}%`;
const num = (v) => Number(v || 0).toLocaleString();
const roundTo = (v, d = 2) => Math.round(Number(v || 0) * 10 ** d) / 10 ** d;

const scoreColor = (v) => {
  const n = Number(v || 0);
  if (n < 60) return C.red;
  if (n < 80) return C.amber;
  return C.green;
};

// ─── SVG Donut chart ─────────────────────────────────────────────────────────
const DonutChart = ({ completionRate = 0, completed = 0, notCompleted = 0, total = 0 }) => {
  const r = 44, sw = 14, circ = 2 * Math.PI * r;
  const arc = Math.min(1, Math.max(0, completionRate / 100)) * circ;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 24, padding: '14px 20px', border: `1px solid ${C.border}`, borderRadius: 8, background: C.white, marginTop: 16 }}>
      <div style={{ position: 'relative', width: 110, height: 110, flexShrink: 0 }}>
        <svg viewBox="0 0 110 110" width={110} height={110}>
          <circle cx="55" cy="55" r={r} fill="none" stroke="#E5E7EB" strokeWidth={sw} />
          <circle cx="55" cy="55" r={r} fill="none" stroke={C.blue} strokeWidth={sw}
            strokeDasharray={`${arc.toFixed(1)} ${circ.toFixed(1)}`}
            strokeLinecap="round"
            transform="rotate(-90 55 55)" />
        </svg>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.blue, lineHeight: 1 }}>{Math.round(completionRate)}%</div>
          <div style={{ fontSize: 9, color: C.textLight, textTransform: 'uppercase', letterSpacing: 0.8 }}>done</div>
        </div>
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.blue, marginBottom: 4 }}>{num(completed)} Completed</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.orange, marginBottom: 6 }}>{num(notCompleted)} Did not complete</div>
        <div style={{ fontSize: 11, color: C.textLight }}>of {num(total)} registered staff</div>
      </div>
    </div>
  );
};

// ─── Shared layout primitives ─────────────────────────────────────────────────
const Section = ({ children, style = {} }) => (
  <div style={{
    background: C.white, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: '24px 28px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', ...style
  }}>
    {children}
  </div>
);

const SectionBand = ({ label, color = C.teal, stats = [] }) => (
  <div style={{
    background: color + '18', borderLeft: `4px solid ${color}`,
    padding: '10px 16px', borderRadius: 4,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14,
  }}>
    <div style={{ fontWeight: 700, fontSize: 13, color, letterSpacing: 0.5 }}>{label}</div>
    {stats.length > 0 && (
      <div style={{ display: 'flex', gap: 32 }}>
        {stats.map(({ val, lbl, color: sc }) => (
          <div key={lbl} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: sc }}>{val}</div>
            <div style={{ fontSize: 10, color: C.textLight }}>{lbl}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const KpiCard = ({ value, label, color = C.teal }) => (
  <div style={{
    flex: 1, background: C.white, border: `1px solid ${C.border}`, borderRadius: 6,
    padding: '14px 20px', textAlign: 'center', minWidth: 110,
  }}>
    <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>{label}</div>
  </div>
);

// ─── Sort toggle ──────────────────────────────────────────────────────────────
const SortToggle = ({ value, onChange, options }) => (
  <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: 6, padding: 2, gap: 2 }}>
    {options.map(({ k, label }) => (
      <button key={k} onClick={() => onChange(k)} style={{
        background: value === k ? C.white : 'transparent',
        border: value === k ? `1px solid ${C.border}` : '1px solid transparent',
        borderRadius: 4, padding: '2px 10px', cursor: 'pointer',
        fontSize: 10, fontWeight: value === k ? 700 : 400,
        color: value === k ? C.text : C.textLight,
        boxShadow: value === k ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
        transition: 'all 0.15s',
      }}>{label}</button>
    ))}
  </div>
);

// ─── Leaderboard section ──────────────────────────────────────────────────────
const LeaderboardSection = ({ leaderboard, deptLabel = 'Department', benchmark = 90, networkAvg = {} }) => {
  const [sortKey, setSortKey] = useState('score');
  const data = (leaderboard?.data || []).filter(p => (Number(p.completionRate) || 0) >= 33);
  const qualified = leaderboard?.totalQualified ?? data.length;
  const withCompletions = leaderboard?.totalWithCompletions ?? data.length;
  const networkScore = Number(networkAvg?.averageScore || 0);

  const sorted = [...data].sort((a, b) => {
    if (sortKey === 'score') return (b.score || 0) - (a.score || 0);
    if (sortKey === 'time') return (a.averageTimeSpent || 0) - (b.averageTimeSpent || 0);
    return 0;
  });

  return (
    <Section>
      <SectionBand
        label="RESULTS LEADERBOARD"
        color={C.navy}
        stats={[
          { val: num(qualified), lbl: 'Qualified', color: C.navy },
          { val: num(withCompletions), lbl: 'With Completions', color: C.teal },
        ]}
      />

      {/* Eligibility note */}
      <div style={{
        fontSize: 11, color: C.textMid, background: C.tealLight,
        border: `1px solid #a7d8e6`, borderRadius: 6,
        padding: '8px 14px', marginBottom: 14,
      }}>
        Employees who completed at least <strong>33%</strong> of assigned campaigns qualify.{' '}
        <strong>{qualified} of {withCompletions}</strong> qualified — ranked by average score across all completed campaigns.
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
          {[
            { el: <div style={{ width: 20, height: 8, background: C.blue, borderRadius: 4 }} />, lbl: 'Score' },
            ...(networkScore > 0 ? [{ el: <div style={{ borderTop: `2px dashed #6b7280`, width: 20 }} />, lbl: `Network avg (${Math.round(networkScore)}%)` }] : []),
            { el: <div style={{ borderTop: `2px dashed ${C.red}`, width: 20 }} />, lbl: `Benchmark (${benchmark}%)` },
          ].map(({ el, lbl }) => (
            <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {el}<span style={{ fontSize: 10, color: C.textLight }}>{lbl}</span>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, color: C.textLight }}>Rank by</span>
          <SortToggle
            value={sortKey}
            onChange={setSortKey}
            options={[{ k: 'score', label: 'Score' }, { k: 'time', label: 'Time' }]}
          />
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: '36px 1fr 120px 180px 72px 70px 70px 70px',
        gap: 8, padding: '6px 0', borderBottom: `2px solid ${C.border}`,
        fontSize: 10, fontWeight: 600, color: C.textLight, textTransform: 'uppercase', letterSpacing: 0.5,
      }}>
        {['#', 'Name', deptLabel, 'Score bar', 'Score', 'Time', 'Done', 'Comp%'].map((h, i) => (
          <div key={h} style={{ textAlign: i >= 4 ? 'right' : 'left' }}>{h}</div>
        ))}
      </div>

      {sorted.map((person, i) => {
        const rank = i + 1;
        const isTop = rank <= 3;
        const sc = Number(person.score || 0);
        const trophy = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;
        return (
          <div key={person.email || person.name || i} style={{
            display: 'grid', gridTemplateColumns: '36px 1fr 120px 180px 72px 70px 70px 70px',
            gap: 8, padding: '7px 6px', borderBottom: `1px solid ${C.border}`, alignItems: 'center',
            background: isTop ? C.greenLight : 'transparent', borderRadius: 4,
          }}>
            <div style={{ fontSize: 12, fontWeight: isTop ? 700 : 400, color: isTop ? C.green : C.textLight, textAlign: 'center' }}>
              {trophy || rank}
            </div>
            <div style={{ fontSize: 12, fontWeight: isTop ? 700 : 400, color: C.text }}>{person.name || '—'}</div>
            <div style={{ fontSize: 11, color: C.textMid }}>{person.department || '—'}</div>
            <div style={{ position: 'relative' }}>
              <div style={{ height: 9, background: '#E5E7EB', borderRadius: 5, overflow: 'visible', position: 'relative' }}>
                <div style={{ width: `${Math.min(sc, 100)}%`, height: '100%', background: C.blue, borderRadius: 5 }} />
                {networkScore > 0 && (
                  <div style={{ position: 'absolute', top: -2, bottom: -2, left: `${Math.min(networkScore, 100)}%`, width: 2, background: '#6b7280', opacity: 0.8 }} />
                )}
                <div style={{ position: 'absolute', top: -2, bottom: -2, left: `${Math.min(benchmark, 100)}%`, width: 2, background: C.red }} />
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 11, fontWeight: sortKey === 'score' ? 700 : 400, color: sortKey === 'score' ? scoreColor(sc) : C.textMid }}>
              {person.scoreDisplay || pct(sc, 0)}
            </div>
            <div style={{ textAlign: 'right', fontSize: 11, color: C.textMid }}>{person.timeSpentDisplay || '—'}</div>
            <div style={{ textAlign: 'right', fontSize: 11, color: C.textLight }}>
              {person.campaignsCompleted ?? person.campaignCount ?? '—'}/{person.campaignsAssigned ?? '—'}
            </div>
            <div style={{ textAlign: 'right', fontSize: 11, color: C.textMid }}>
              {person.completionRateDisplay || pct(person.completionRate || 0, 0)}
            </div>
          </div>
        );
      })}
      {sorted.length === 0 && (
        <div style={{ textAlign: 'center', padding: 24, color: C.textLight, fontSize: 12 }}>No qualified participants</div>
      )}
    </Section>
  );
};

// ─── At-risk / non-starters section ──────────────────────────────────────────
const AtRiskSection = ({ nonStarters = [] }) => {
  if (!nonStarters.length) return null;
  const notStarted = nonStarters.filter(p => p.campaignsCompleted === 0).length;
  return (
    <Section>
      <SectionBand
        label="DID NOT QUALIFY FOR LEADERBOARD"
        color={C.red}
        stats={[
          { val: num(nonStarters.length), lbl: 'Employees', color: C.red },
          { val: num(notStarted), lbl: 'Not Started', color: C.textLight },
        ]}
      />
      <div style={{ fontSize: 11, color: C.textMid, background: C.amberLight, border: `1px solid #FDE68A`, borderRadius: 6, padding: '8px 14px', marginBottom: 14 }}>
        These employees did not complete <strong>33%</strong> of their assigned campaigns and are excluded from the leaderboard.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 70px 60px 60px', gap: 8, padding: '6px 0', borderBottom: `2px solid ${C.border}`, fontSize: 10, fontWeight: 600, color: C.textLight, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {['Name', 'Department', 'Done/Asgn', 'CR%', 'Score'].map((h, i) => (
          <div key={h} style={{ textAlign: i >= 2 ? 'right' : 'left' }}>{h}</div>
        ))}
      </div>
      {nonStarters.map((p, i) => (
        <div key={p.email || i} style={{ display: 'grid', gridTemplateColumns: '1fr 140px 70px 60px 60px', gap: 8, padding: '7px 6px', borderBottom: `1px solid ${C.border}`, alignItems: 'center' }}>
          <div style={{ fontSize: 12, color: C.text, fontWeight: 500 }}>{p.name || '—'}</div>
          <div style={{ fontSize: 11, color: C.textMid }}>{p.department || '—'}</div>
          <div style={{ fontSize: 11, color: C.textLight, textAlign: 'right' }}>{p.campaignsCompleted ?? 0}/{p.campaignsAssigned}</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: p.completionRate === 0 ? C.red : C.amber, textAlign: 'right' }}>
            {p.completionRateDisplay || `${Math.round(p.completionRate || 0)}%`}
          </div>
          <div style={{ fontSize: 11, fontWeight: 600, color: scoreColor(p.score || 0), textAlign: 'right' }}>
            {p.scoreDisplay || `${Math.round(p.score || 0)}%`}
          </div>
        </div>
      ))}
    </Section>
  );
};

// ─── Score distribution buckets ──────────────────────────────────────────────
const ScoreDistributionSection = ({ scoreBuckets = {} }) => {
  const { green = 0, amber = 0, red = 0, total = 0 } = scoreBuckets;
  if (total === 0) return null;
  const bar = (v) => total > 0 ? `${Math.round((v / total) * 100)}%` : '0%';
  const zones = [
    { label: 'High performers', sub: '≥ 80% eff. score', count: green, color: C.green, bg: C.greenLight },
    { label: 'Developing', sub: '60 – 79%', count: amber, color: C.amber, bg: C.amberLight },
    { label: 'At risk', sub: '< 60%', count: red, color: C.red, bg: C.redLight },
  ];
  return (
    <Section>
      <SectionBand label="SCORE DISTRIBUTION" color={C.teal} stats={[{ val: num(total), lbl: 'Qualified', color: C.teal }]} />
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        {zones.map(z => (
          <div key={z.label} style={{ flex: 1, minWidth: 140, background: z.bg, border: `1px solid ${z.color}33`, borderRadius: 8, padding: '16px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: z.color, lineHeight: 1 }}>{num(z.count)}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: z.color, marginTop: 4 }}>{z.label}</div>
            <div style={{ fontSize: 10, color: C.textLight, marginTop: 2 }}>{z.sub}</div>
            <div style={{ fontSize: 11, color: z.color, marginTop: 6, fontWeight: 600 }}>{bar(z.count)} of team</div>
          </div>
        ))}
      </div>
      <div style={{ background: C.bg, borderRadius: 8, padding: '12px 16px' }}>
        <div style={{ display: 'flex', height: 24, borderRadius: 6, overflow: 'hidden', gap: 2 }}>
          {zones.map(z => z.count > 0 && (
            <div key={z.label} style={{ flex: z.count, background: z.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {z.count > 0 && <span style={{ fontSize: 10, color: '#fff', fontWeight: 700 }}>{bar(z.count)}</span>}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
          {zones.map(z => (
            <div key={z.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: z.color }} />
              <span style={{ fontSize: 10, color: C.textLight }}>{z.label} ({z.count})</span>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};

// ─── Campaign breakdown section ───────────────────────────────────────────────
const CampaignBreakdownSection = ({ campaignBreakdown = [], benchmark = 90 }) => {
  if (!campaignBreakdown.length) return null;
  return (
    <Section>
      <SectionBand label="CAMPAIGN BREAKDOWN" color={C.navy} stats={[{ val: num(campaignBreakdown.length), lbl: 'Campaigns', color: C.navy }]} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px 80px 80px 80px', gap: 8, padding: '6px 0', borderBottom: `2px solid ${C.border}`, fontSize: 10, fontWeight: 600, color: C.textLight, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {['Campaign', 'Completion bar', 'Assigned', 'CR%', 'Avg Score'].map((h, i) => (
          <div key={h} style={{ textAlign: i >= 2 ? 'right' : 'left' }}>{h}</div>
        ))}
      </div>
      {campaignBreakdown.map((c, i) => {
        const cr = Number(c.completionRate || 0);
        const sc = Number(c.avgScore || 0);
        const crColor = cr >= benchmark ? C.green : cr >= benchmark * 0.7 ? C.amber : C.red;
        return (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 180px 80px 80px 80px', gap: 8, padding: '8px 6px', borderBottom: `1px solid ${C.border}`, alignItems: 'center' }}>
            <div style={{ fontSize: 12, color: C.text, fontWeight: 500 }}>{c.name}</div>
            <div style={{ position: 'relative' }}>
              <div style={{ height: 9, background: '#E5E7EB', borderRadius: 5, overflow: 'visible', position: 'relative' }}>
                <div style={{ width: `${Math.min(cr, 100)}%`, height: '100%', background: crColor, borderRadius: 5 }} />
                <div style={{ position: 'absolute', top: -2, bottom: -2, left: `${Math.min(benchmark, 100)}%`, width: 2, background: C.red }} />
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 11, color: C.textLight }}>{num(c.assigned)}</div>
            <div style={{ textAlign: 'right', fontSize: 12, fontWeight: 700, color: crColor }}>{pct(cr, 0)}</div>
            <div style={{ textAlign: 'right', fontSize: 11, color: scoreColor(sc) }}>{pct(sc, 0)}</div>
          </div>
        );
      })}
    </Section>
  );
};

// ─── BU AI insight section ────────────────────────────────────────────────────
const BuAiInsightSection = ({ insight, loading, error, onGenerate, onInsightChange }) => {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState('');

  const startEdit = (field, val) => { setEditing(field); setDraft(val || ''); };
  const commitEdit = (field) => {
    if (!insight) return;
    let updated;
    if (typeof field === 'number') {
      const areas = [...(insight.recommended_focus_areas || [])];
      areas[field] = draft;
      updated = { ...insight, recommended_focus_areas: areas };
    } else {
      updated = { ...insight, [field]: draft };
    }
    onInsightChange && onInsightChange(updated);
    setEditing(null);
  };

  const EditableField = ({ fieldKey, value }) => editing === fieldKey ? (
    <div>
      <textarea autoFocus value={draft} onChange={e => setDraft(e.target.value)} rows={4}
        style={{ width: '100%', boxSizing: 'border-box', fontSize: 12, lineHeight: 1.7, padding: '10px 12px', borderRadius: 6, border: `2px solid ${C.navy}`, resize: 'vertical', fontFamily: 'inherit', color: C.text }} />
      <div style={{ display: 'flex', gap: 8, marginTop: 6, justifyContent: 'flex-end' }}>
        <button onClick={() => commitEdit(fieldKey)} style={{ background: C.navy, color: C.white, border: 'none', borderRadius: 4, padding: '4px 14px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>✓ Save</button>
        <button onClick={() => setEditing(null)} style={{ background: 'transparent', color: C.textLight, border: `1px solid ${C.border}`, borderRadius: 4, padding: '4px 14px', cursor: 'pointer', fontSize: 11 }}>Cancel</button>
      </div>
    </div>
  ) : (
    <div onClick={() => insight && startEdit(fieldKey, value)} title={insight ? 'Click to edit' : ''}
      style={{ background: C.tealLight, border: `1px solid ${C.teal}22`, borderRadius: 6, padding: '14px 16px', fontSize: 12, color: C.text, lineHeight: 1.7, cursor: insight ? 'text' : 'default', position: 'relative' }}>
      {value}
      {insight && <span style={{ position: 'absolute', top: 8, right: 10, fontSize: 10, color: C.textLight, opacity: 0.6 }}>✎</span>}
    </div>
  );

  const PlaceholderBox = () => (
    <div style={{ background: C.bg, border: `1px dashed ${C.border}`, borderRadius: 6, padding: '20px 16px', color: C.textLight, fontSize: 12, fontStyle: 'italic', textAlign: 'center' }}>
      {loading ? 'Analysing report data…' : 'Click "Generate AI Insights" to populate this section'}
    </div>
  );

  return (
    <Section>
      <div style={{ background: C.navy + '18', borderLeft: `4px solid ${C.navy}`, padding: '10px 16px', borderRadius: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: C.navy, letterSpacing: 0.5 }}>✦ INSIGHTS</div>
        {!insight && !loading && (
          <button onClick={onGenerate} style={{ background: C.navy, color: C.white, border: 'none', borderRadius: 5, padding: '7px 18px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            ✦ Generate AI Insights
          </button>
        )}
        {insight && (
          <button onClick={onGenerate} style={{ background: 'transparent', color: C.navy, border: `1px solid ${C.navy}`, borderRadius: 5, padding: '5px 14px', cursor: 'pointer', fontSize: 11 }}>
            Regenerate
          </button>
        )}
      </div>

      {error && <div style={{ color: C.red, fontSize: 12, marginBottom: 12 }}>{error}</div>}

      {loading && (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Spin />
          <div style={{ marginTop: 10, fontSize: 12, color: C.textLight }}>Analysing report data…</div>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 8 }}>Overall Trend Insight</div>
        {insight?.overall_trend_insight ? <EditableField fieldKey="overall_trend_insight" value={insight.overall_trend_insight} /> : <PlaceholderBox />}
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 8 }}>Strategic Summary</div>
        {insight?.strategic_summary ? <EditableField fieldKey="strategic_summary" value={insight.strategic_summary} /> : <PlaceholderBox />}
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 8 }}>Recommended Focus Areas</div>
        {insight?.recommended_focus_areas?.length
          ? <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {insight.recommended_focus_areas.map((area, i) => (
                editing === i ? (
                  <div key={i}>
                    <textarea autoFocus value={draft} onChange={e => setDraft(e.target.value)} rows={3}
                      style={{ width: '100%', boxSizing: 'border-box', fontSize: 12, lineHeight: 1.6, padding: '8px 12px', borderRadius: 6, border: `2px solid ${C.navy}`, resize: 'vertical', fontFamily: 'inherit', color: C.text }} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 6, justifyContent: 'flex-end' }}>
                      <button onClick={() => commitEdit(i)} style={{ background: C.navy, color: C.white, border: 'none', borderRadius: 4, padding: '4px 14px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>✓ Save</button>
                      <button onClick={() => setEditing(null)} style={{ background: 'transparent', color: C.textLight, border: `1px solid ${C.border}`, borderRadius: 4, padding: '4px 14px', cursor: 'pointer', fontSize: 11 }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div key={i} onClick={() => startEdit(i, area)}
                    style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: '10px 14px', fontSize: 12, color: C.text, lineHeight: 1.6, cursor: 'text', position: 'relative' }}>
                    <span style={{ fontWeight: 700, color: C.navy, marginRight: 6 }}>{i + 1}.</span>{area}
                    <span style={{ position: 'absolute', top: 8, right: 10, fontSize: 10, color: C.textLight, opacity: 0.6 }}>✎</span>
                  </div>
                )
              ))}
            </div>
          : <PlaceholderBox />}
      </div>

      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>Prepared by TapLearnGo</div>
          <div style={{ fontSize: 11, color: C.textLight }}>Learning &amp; Performance Analytics</div>
        </div>
        <div style={{ fontSize: 11, color: C.textLight }}>{new Date().toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
      </div>
    </Section>
  );
};

// ─── Departments section ──────────────────────────────────────────────────────
const DepartmentsSection = ({ departments, benchmark = 90, deptLabel = 'Department' }) => {
  const [sortKey, setSortKey] = useState('completionRateDisplay');
  const data = departments?.data || [];
  const sorted = [...data].sort((a, b) => {
    if (sortKey === 'completionRateDisplay') return (b.completionRate || 0) - (a.completionRate || 0);
    if (sortKey === 'averageScoreDisplay') return (b.averageScore || 0) - (a.averageScore || 0);
    return 0;
  });

  return (
    <Section>
      <SectionBand
        label={`PERFORMANCE BY ${deptLabel.toUpperCase()}`}
        color={C.teal}
        stats={[
          { val: num(data.length), lbl: `${deptLabel}s`, color: C.teal },
        ]}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
          {[
            { el: <div style={{ width: 20, height: 8, background: C.blue, borderRadius: 4 }} />, lbl: 'Completion rate' },
            { el: <div style={{ width: 20, height: 8, background: C.navy, borderRadius: 4 }} />, lbl: 'Average score' },
            { el: <div style={{ borderTop: `2px dashed ${C.red}`, width: 20 }} />, lbl: `Benchmark (${benchmark}%)` },
          ].map(({ el, lbl }) => (
            <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{el}<span style={{ fontSize: 10, color: C.textLight }}>{lbl}</span></div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, color: C.textLight }}>Rank by</span>
          <SortToggle
            value={sortKey}
            onChange={setSortKey}
            options={[{ k: 'completionRateDisplay', label: 'Completion' }, { k: 'averageScoreDisplay', label: 'Score' }]}
          />
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: '32px 1fr 80px 70px 280px 80px 80px',
        gap: 8, padding: '6px 0', borderBottom: `2px solid ${C.border}`,
        fontSize: 10, fontWeight: 600, color: C.textLight, textTransform: 'uppercase', letterSpacing: 0.5,
      }}>
        {['#', deptLabel, 'Staff', 'Done', 'Rates', 'Comp%', 'Score%'].map((h, i) => {
          const isComp = h === 'Comp%';
          const isScore = h === 'Score%';
          const isActive = (isComp && sortKey === 'completionRateDisplay') || (isScore && sortKey === 'averageScoreDisplay');
          return (
            <div key={h} style={{ textAlign: i >= 4 ? 'right' : 'left', color: isActive ? C.teal : C.textLight }}>
              {h}{isActive ? ' ▼' : ''}
            </div>
          );
        })}
      </div>

      {sorted.map((dept, i) => {
        const rank = i + 1;
        const isTop = rank <= 3;
        const cr = Number(dept.completionRate || 0);
        const sc = Number(dept.averageScore || 0);
        const isLow = rank > sorted.length - 3 && sorted.length > 6 && (sortKey === 'completionRateDisplay' ? cr : sc) < 30;
        return (
          <div key={dept.department || i} style={{
            display: 'grid', gridTemplateColumns: '32px 1fr 80px 70px 280px 80px 80px',
            gap: 8, padding: '7px 6px', borderBottom: `1px solid ${C.border}`, alignItems: 'center',
            background: isTop ? C.greenLight : isLow ? C.redLight : 'transparent', borderRadius: 4,
          }}>
            <div style={{ fontSize: 12, fontWeight: isTop ? 700 : 400, color: isTop ? C.green : C.textLight, textAlign: 'center' }}>{rank}</div>
            <div style={{ fontSize: 12, fontWeight: isTop ? 700 : 400, color: C.text }}>{dept.department || '—'}</div>
            <div style={{ fontSize: 11, color: C.textMid, textAlign: 'right' }}>{num(dept.total)}</div>
            <div style={{ fontSize: 11, color: C.green, textAlign: 'right', fontWeight: 600 }}>{num(dept.completed)}</div>
            <div style={{ position: 'relative' }}>
              <div style={{ height: 9, background: '#E5E7EB', borderRadius: 5, marginBottom: 3, position: 'relative', overflow: 'visible' }}>
                <div style={{ width: `${Math.min(cr, 100)}%`, height: '100%', background: C.blue, borderRadius: 5 }} />
                <div style={{ position: 'absolute', top: -2, bottom: -2, left: `${benchmark}%`, width: 2, background: C.red }} />
              </div>
              <div style={{ height: 9, background: '#E5E7EB', borderRadius: 5, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(sc, 100)}%`, height: '100%', background: C.navy, borderRadius: 5 }} />
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 11, fontWeight: sortKey === 'completionRateDisplay' ? 700 : 400, color: sortKey === 'completionRateDisplay' ? C.teal : C.textMid }}>
              {dept.completionRateDisplay || pct(cr, 0)}
            </div>
            <div style={{ textAlign: 'right', fontSize: 11, fontWeight: sortKey === 'averageScoreDisplay' ? 700 : 400, color: sortKey === 'averageScoreDisplay' ? C.teal : C.textMid }}>
              {dept.averageScoreDisplay || pct(sc, 0)}
            </div>
          </div>
        );
      })}
      {sorted.length === 0 && <div style={{ textAlign: 'center', padding: 24, color: C.textLight, fontSize: 12 }}>No department data available</div>}
    </Section>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const BusinessUnitReport = ({
  data,
  defaultReportName = 'Business Unit Analysis Report',
  benchmark = 90,
}) => {
  const [reportName, setReportName] = useState(defaultReportName);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(defaultReportName);
  const [exporting, setExporting] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);

  // PDF customisation
  const [pdfLabel, setPdfLabel] = useState('Dealership Results Dashboard');
  const [pdfOrgLabel, setPdfOrgLabel] = useState('Dealership');
  const [pdfReportSubtitle, setPdfReportSubtitle] = useState('Performance Report');
  const [pdfTemplate, setPdfTemplate] = useState('default');
  const [pdfProgramTitle, setPdfProgramTitle] = useState('');
  const [pdfClientLogoUrl, setPdfClientLogoUrl] = useState('');
  const [pdfSettingsOpen, setPdfSettingsOpen] = useState(false);
  const [pdfProfiles, setPdfProfiles] = useState([]);
  const [pdfProfileName, setPdfProfileName] = useState('');
  const [savePdfProfileOpen, setSavePdfProfileOpen] = useState(false);

  useEffect(() => {
    settingsService.getPdfProfiles().then(result => {
      setPdfProfiles(Array.isArray(result.data) ? result.data : []);
    });
  }, []);

  // AI insights
  const [buAiInsight, setBuAiInsight] = useState(null);
  const [buAiLoading, setBuAiLoading] = useState(false);
  const [buAiError, setBuAiError] = useState(null);

  useEffect(() => {
    setReportName(defaultReportName);
    setDraft(defaultReportName);
  }, [defaultReportName]);

  const commitTitle = () => {
    const val = draft.trim();
    if (val) setReportName(val);
    setEditing(false);
  };

  const generateBuInsight = useCallback(async () => {
    const org = data?.organization || {};
    const period = data?.period || {};
    const overview = data?.overview || {};
    const leaderboard = data?.leaderboard || {};
    const networkAvg = data?.networkAvg || {};
    setBuAiLoading(true);
    setBuAiError(null);
    try {
      const result = await reportService.getBuAIInsights({
        orgName: org.name || '',
        period: period.display || '',
        completionRate: Math.round(overview.completionRate || 0),
        avgScore: Math.round(overview.averageScore || 0),
        benchmark,
        networkAvgCr: Math.round(networkAvg.completionRate || 0),
        networkAvgScore: Math.round(networkAvg.averageScore || 0),
        totalStaff: overview.uniqueParticipants || 0,
        nonStartersCount: (data?.nonStarters || []).length,
        scoreBuckets: data?.scoreBuckets || {},
        campaignBreakdown: (data?.campaignBreakdown || []).map(c => ({
          name: c.name,
          completionRate: Math.round(c.completionRate || 0),
          avgScore: Math.round(c.avgScore || 0),
          assigned: c.assigned,
          completed: c.completed,
        })),
        departments: (data?.departments?.data || []).slice(0, 6).map(d => ({
          name: d.department || '',
          completionRate: Math.round(d.completionRate || 0),
          avgScore: Math.round(d.averageScore || 0),
        })),
        topPerformers: (leaderboard.data || []).slice(0, 3).map(p => p.name),
        timingStats: data?.timingStats || null,
        priorMonths: [data?.comparison?.month2, data?.comparison?.month1]
          .filter(Boolean)
          .map(m => ({
            label: m.label || '',
            completionRate: Math.round(m.completionRate || 0),
            avgScore: Math.round(m.averageScore || 0),
          })),
        // Named per prompt rule 6 — zero-completion non-qualifiers specifically,
        // not the broader <33%-completion non-starters list.
        nonQualifierNames: (data?.nonStarters || [])
          .filter(p => (p.campaignsCompleted || 0) === 0)
          .slice(0, 15)
          .map(p => p.name),
        lowestScorers: (leaderboard.data || [])
          .slice()
          .sort((a, b) => a.score - b.score)
          .slice(0, 5)
          .map(p => ({ name: p.name, score: Math.round(p.score || 0) })),
        // Question text + correct-% per campaign, so the AI can do a real thematic
        // read instead of guessing from scores alone (see prompt's INPUT NOTE).
        campaignQuestions: (data?.campaignDetails || []).map(c => ({
          name: c.name,
          questions: (c.questions || []).map(q => ({
            questionText: q.questionText,
            successRateDisplay: q.successRateDisplay,
          })),
        })),
      });
      if (result.success) {
        setBuAiInsight(result.data);
      } else {
        setBuAiError(result.message || 'Failed to generate insights');
      }
    } catch {
      setBuAiError('Failed to generate insights');
    }
    setBuAiLoading(false);
  }, [data, benchmark]);

  const handleSavePdfProfile = useCallback(async () => {
    const name = pdfProfileName.trim();
    if (!name) { message.warning('Please enter a profile name'); return; }
    const profile = { name, reportLabel: pdfLabel, orgLabel: pdfOrgLabel, reportSubtitle: pdfReportSubtitle, programTitle: pdfProgramTitle, reportName, clientLogoUrl: pdfClientLogoUrl };
    const result = await settingsService.savePdfProfile(profile);
    if (result.success !== false) {
      const refreshed = await settingsService.getPdfProfiles();
      setPdfProfiles(Array.isArray(refreshed.data) ? refreshed.data : []);
      setPdfProfileName('');
      setSavePdfProfileOpen(false);
      message.success(`Profile "${name}" saved`);
    } else {
      message.error(result.message || 'Failed to save profile');
    }
  }, [pdfProfileName, pdfLabel, pdfOrgLabel, pdfReportSubtitle, pdfProgramTitle, pdfClientLogoUrl]);

  const handleLoadPdfProfile = useCallback((name) => {
    const profile = pdfProfiles.find(p => p.name === name);
    if (!profile) return;
    if (profile.reportLabel !== undefined) setPdfLabel(profile.reportLabel || 'Dealership Results Dashboard');
    if (profile.orgLabel !== undefined) setPdfOrgLabel(profile.orgLabel || 'Dealership');
    if (profile.reportSubtitle !== undefined) setPdfReportSubtitle(profile.reportSubtitle || 'Performance Report');
    if (profile.programTitle !== undefined) setPdfProgramTitle(profile.programTitle || '');
    if (profile.clientLogoUrl !== undefined) setPdfClientLogoUrl(profile.clientLogoUrl || '');
    if (profile.reportName) setReportName(profile.reportName);
  }, [pdfProfiles]);

  const handleDeletePdfProfile = useCallback(async (name) => {
    const result = await settingsService.deletePdfProfile(name);
    if (result.success !== false) {
      const refreshed = await settingsService.getPdfProfiles();
      setPdfProfiles(Array.isArray(refreshed.data) ? refreshed.data : []);
      message.success(`Profile "${name}" deleted`);
    } else {
      message.error(result.message || 'Failed to delete profile');
    }
  }, []);

  const exportDocx = useCallback(async (reportTitle) => {
    try {
      const organization = data?.organization || {};
      const period = data?.period || {};
      const overview = data?.overview || {};
      const leaderboard = data?.leaderboard || { data: [], scoreChart: {}, completionRateChart: {} };
      const departments = data?.departments || { data: [], completionRateChart: {}, scoreChart: {}, peopleChart: {} };
      const campaigns = data?.campaigns || [];
      const comparison = data?.comparison || {};

      const staffCountChart = { labels: ['Total Staff'], datasets: [{ label: 'Staff Count', data: [overview.uniqueParticipants || 0], dataLabels: [(overview.uniqueParticipants || 0).toString()] }] };
      const averageScoreChart = { labels: ['Average Score'], datasets: [{ label: 'Average Score', data: [overview.averageScore || 0], dataLabels: [overview.averageScoreDisplay || '0%'] }] };
      const completionRateChart = { labels: ['Completion Rate'], datasets: [{ label: 'Completion Rate', data: [overview.completionRate || 0], dataLabels: [overview.completionRateDisplay || '0%'] }] };
      const averageTimeSpentChart = { labels: ['Average Time'], datasets: [{ label: 'Average Time Spent', data: [overview.averageTimeSpent || 0], dataLabels: [overview.averageTimeSpentDisplay || '0 min'] }] };

      const mkComparisonChart = (field, displayField, labelSuffix) => ({
        labels: [comparison.month2?.label || 'Month -2', comparison.month1?.label || 'Month -1', comparison.currentMonth?.label || 'Current', comparison.managingGroupAvg?.label || 'Group Avg', comparison.benchmarks?.label || 'Benchmark'],
        datasets: [{ label: labelSuffix, data: [comparison.month2?.[field] || 0, comparison.month1?.[field] || 0, comparison.currentMonth?.[field] || 0, comparison.managingGroupAvg?.[field] || 0, comparison.benchmarks?.[field] || 0], dataLabels: [comparison.month2?.[displayField] || '0%', comparison.month1?.[displayField] || '0%', comparison.currentMonth?.[displayField] || '0%', comparison.managingGroupAvg?.[displayField] || '0%', comparison.benchmarks?.[displayField] || '0%'] }]
      });

      const payload = {
        template: { name: 'ORG quiz analysis Docx' },
        reportName: reportTitle,
        organization: { name: organization.name || 'Organization', participantCount: organization.participantCount || 0, departmentCount: (organization.departments || []).length, managingGroup: organization.managingGroup || '' },
        period: { month: period.month || '', year: period.year || '', display: period.display || '' },
        overview: { totalTargets: overview.uniqueParticipants || 0, uniqueParticipants: overview.uniqueParticipants || 0, completed: overview.completed || 0, completionRate: overview.completionRate || 0, completionRateDisplay: overview.completionRateDisplay || '0%', averageScore: overview.averageScore || 0, averageScoreDisplay: overview.averageScoreDisplay || '0%', averageTimeSpent: overview.averageTimeSpent || 0, averageTimeSpentDisplay: overview.averageTimeSpentDisplay || '0 min', deliveryRate: overview.targetDelivery?.deliveryRate || '0%' },
        campaigns: campaigns.map(c => ({ name: c.name, status: c.status })),
        charts: { staffCount: staffCountChart, averageScore: averageScoreChart, completionRate: completionRateChart, averageTimeSpent: averageTimeSpentChart, completionRateComparison: mkComparisonChart('completionRate', 'completionRateDisplay', 'Completion Rate'), scoreComparison: mkComparisonChart('averageScore', 'averageScoreDisplay', 'Average Score'), leaderboardScore: leaderboard.scoreChart || { labels: [], datasets: [] }, leaderboardCompletionRate: leaderboard.completionRateChart || { labels: [], datasets: [] }, departmentsCompletionRate: departments.completionRateChart || { labels: [], datasets: [] }, departmentsScore: departments.scoreChart || { labels: [], datasets: [] }, departmentsPeopleCount: departments.peopleChart || { labels: [], datasets: [] } }
      };

      const res = await apiClient.post('/reports/export-pptx', payload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      const safeName = (reportTitle || 'BU Quiz Analysis').replace(/[^a-zA-Z0-9-_\s]/g, '').trim().replace(/\s+/g, '_');
      const now = new Date();
      const ts = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}-${String(now.getMinutes()).padStart(2,'0')}`;
      a.href = url; a.download = `${safeName}_${ts}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      throw error;
    }
  }, [data]);

  const handleExport = useCallback(async () => {
    const trimmed = reportName.trim();
    if (!trimmed) { message.warning('Please provide a report name'); return; }
    setExporting(true);
    try {
      await exportDocx(trimmed);
      message.success('Report exported successfully');
    } catch {
      message.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  }, [reportName, exportDocx]);

  const handleExportPdf = useCallback(async () => {
    const trimmed = reportName.trim();
    if (!trimmed) { message.warning('Please provide a report name'); return; }
    const organization = data?.organization || {};
    const period = data?.period || {};
    const overview = data?.overview || {};
    const leaderboard = data?.leaderboard || { data: [] };
    const departments = data?.departments || { data: [] };
    const campaigns = data?.campaigns || [];
    const derivedCampaignName = campaigns.map(c => c.name).join(', ');
    setPdfExporting(true);
    try {
      const result = await reportService.exportBuPdf({
        reportName: trimmed,
        orgLabel: pdfOrgLabel || 'Dealership',
        reportLabel: pdfLabel || 'Dealership Results Dashboard',
        reportSubtitle: pdfReportSubtitle || 'Performance Report',
        campaignName: pdfProgramTitle.trim() || derivedCampaignName,
        organization, period, overview, leaderboard, departments,
        comparison: data?.comparison || {},
        allParticipants: data?.allParticipants || [],
        nonStarters: data?.nonStarters || [],
        scoreBuckets: data?.scoreBuckets || {},
        campaignBreakdown: data?.campaignBreakdown || [],
        campaignDetails: data?.campaignDetails || [],
        clientLogoUrl: pdfClientLogoUrl || '',
        aiInsight: buAiInsight || null,
        template: pdfTemplate,
      });
      if (result.success) message.success('PDF exported successfully');
      else message.error(result.message || 'Failed to export PDF');
    } catch {
      message.error('Failed to export PDF');
    } finally {
      setPdfExporting(false);
    }
  }, [reportName, data, pdfOrgLabel, pdfLabel, pdfReportSubtitle, pdfProgramTitle, pdfClientLogoUrl, buAiInsight, pdfTemplate]);

  if (!data) return null;

  const organization = data.organization || {};
  const period = data.period || {};
  const overview = data.overview || {};
  const leaderboard = data.leaderboard || { data: [] };
  const departments = data.departments || { data: [] };
  const campaigns = data.campaigns || [];
  const networkAvg = data.networkAvg || {};
  const nonStarters = data.nonStarters || [];
  const scoreBuckets = data.scoreBuckets || {};
  const campaignBreakdown = data.campaignBreakdown || [];

  const isExporting = exporting || pdfExporting;

  return (
    <>
      {/* Sticky action toolbar */}
      <div className="no-print" style={{
        position: 'sticky', top: 52, zIndex: 100,
        background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(8px)',
        borderBottom: `1px solid ${C.border}`, boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
        padding: '8px 20px', marginBottom: 20, borderRadius: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* PDF Settings gear */}
          <Popover
            open={pdfSettingsOpen}
            onOpenChange={setPdfSettingsOpen}
            trigger="click"
            placement="bottomRight"
            content={(
              <div style={{ width: 320, padding: '4px 0' }}>
                {/* Profiles */}
                {pdfProfiles.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Load saved profile</div>
                    <Select
                      size="small"
                      style={{ width: '100%' }}
                      placeholder="Select a profile…"
                      options={pdfProfiles.map(p => ({ value: p.name, label: p.name }))}
                      onSelect={handleLoadPdfProfile}
                      allowClear
                    />
                  </div>
                )}

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>PDF template</div>
                  <Select
                    size="small"
                    style={{ width: '100%' }}
                    value={pdfTemplate}
                    onChange={setPdfTemplate}
                    options={[
                      { value: 'default', label: 'Default' },
                      { value: 'mbsa', label: 'MBSA Dealership' },
                    ]}
                  />
                </div>

                <Divider style={{ margin: '8px 0', fontSize: 10, color: '#bbb' }}>PDF Labels</Divider>

                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Dashboard label (cover top)</div>
                  <Input size="small" placeholder="Dealership Results Dashboard" value={pdfLabel} onChange={e => setPdfLabel(e.target.value)} />
                </div>

                <Row gutter={8} style={{ marginBottom: 10 }}>
                  <Col span={14}>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Report subtitle</div>
                    <Input size="small" placeholder="Performance Report" value={pdfReportSubtitle} onChange={e => setPdfReportSubtitle(e.target.value)} />
                  </Col>
                  <Col span={10}>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Org label (e.g. Shop)</div>
                    <Input size="small" placeholder="Dealership" value={pdfOrgLabel} onChange={e => setPdfOrgLabel(e.target.value)} />
                  </Col>
                </Row>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Program / campaign title override</div>
                  <Input size="small" placeholder={campaigns.map(c => c.name).join(', ') || 'e.g. MBSA Micro Learning'} value={pdfProgramTitle} onChange={e => setPdfProgramTitle(e.target.value)} />
                  <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>Leave blank to use campaign names</div>
                </div>

                <Divider style={{ margin: '8px 0', fontSize: 10, color: '#bbb' }}>Client Logo</Divider>

                <div style={{ marginBottom: 12 }}>
                  {pdfClientLogoUrl ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <img src={pdfClientLogoUrl} alt="logo" style={{ height: 32, maxWidth: 100, objectFit: 'contain', border: '1px solid #eee', borderRadius: 3, padding: 3 }} />
                      <span style={{ fontSize: 11, color: C.red, cursor: 'pointer' }} onClick={() => setPdfClientLogoUrl('')}>Remove</span>
                    </div>
                  ) : (
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', border: '1px solid #d9d9d9', borderRadius: 4, cursor: 'pointer', fontSize: 11, color: '#555', background: '#fafafa' }}>
                      📎 Upload logo
                      <input type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={e => { const f = e.target.files?.[0]; if (!f) return; const r = new FileReader(); r.onload = ev => setPdfClientLogoUrl(ev.target.result); r.readAsDataURL(f); }} />
                    </label>
                  )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                  <span style={{ fontSize: 11, color: C.teal, cursor: 'pointer' }} onClick={() => { setPdfSettingsOpen(false); setSavePdfProfileOpen(true); }}>
                    Save as profile…
                  </span>
                  <span style={{ fontSize: 11, color: C.textLight, cursor: 'pointer' }} onClick={() => { setPdfLabel('Dealership Results Dashboard'); setPdfOrgLabel('Dealership'); setPdfReportSubtitle('Performance Report'); setPdfProgramTitle(''); setPdfClientLogoUrl(''); setPdfTemplate('default'); }}>
                    Reset
                  </span>
                </div>
              </div>
            )}
          >
            <button style={{
              background: 'white', color: C.textMid,
              border: `1px solid ${C.border}`, borderRadius: 6,
              padding: '7px 11px', cursor: 'pointer', fontSize: 14,
            }} title="PDF Settings">⚙</button>
          </Popover>

          {/* Export Word */}
          <button
            onClick={handleExport}
            disabled={isExporting}
            style={{
              background: isExporting ? '#f5f5f5' : 'white',
              color: isExporting ? C.textLight : C.textMid,
              border: `1px solid ${C.border}`, borderRadius: 6,
              padding: '7px 14px', cursor: isExporting ? 'not-allowed' : 'pointer',
              fontSize: 12, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 5,
            }}
          >
            {exporting ? <Spin size="small" /> : null}
            {exporting ? 'Exporting…' : '⬇ Word'}
          </button>

          {/* Export PDF */}
          <button
            onClick={handleExportPdf}
            disabled={isExporting}
            style={{
              background: isExporting ? '#cce8f2' : C.teal,
              color: isExporting ? C.teal : C.white,
              border: 'none', borderRadius: 6,
              padding: '7px 16px', cursor: isExporting ? 'not-allowed' : 'pointer',
              fontSize: 12, fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 5,
              boxShadow: isExporting ? 'none' : '0 2px 8px rgba(38,122,150,0.3)',
            }}
          >
            {pdfExporting ? <Spin size="small" /> : null}
            {pdfExporting ? 'Exporting…' : 'Export PDF'}
          </button>
        </div>
      </div>

      {/* Report body */}
      <div style={{ fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif", color: C.text, background: C.bg, padding: '0 0 40px' }}>

        {/* Page header */}
        <Section>
          <div style={{ marginBottom: 0 }}>
            <div style={{ fontSize: 10, color: C.textLight, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
              BUSINESS UNIT REPORT
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                {/* Inline editable title */}
                {editing ? (
                  <input
                    autoFocus value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onBlur={commitTitle}
                    onKeyDown={e => { if (e.key === 'Enter') commitTitle(); if (e.key === 'Escape') setEditing(false); }}
                    style={{
                      fontSize: 22, fontWeight: 700, color: C.text,
                      border: 'none', borderBottom: `2px solid ${C.teal}`,
                      outline: 'none', background: 'transparent',
                      width: '100%', padding: '2px 0', fontFamily: 'inherit', marginBottom: 2,
                    }}
                  />
                ) : (
                  <div
                    onClick={() => { setDraft(reportName); setEditing(true); }}
                    title="Click to edit title"
                    style={{ fontSize: 22, fontWeight: 700, color: C.text, marginBottom: 2, cursor: 'text', display: 'inline-flex', alignItems: 'center', gap: 8 }}
                  >
                    {reportName}
                    <span style={{ fontSize: 13, color: C.textLight, fontWeight: 400, opacity: 0.5 }}>✎</span>
                  </div>
                )}
                <div style={{ fontSize: 13, color: C.orange, fontWeight: 600 }}>{organization.name} — {period.display}</div>
              </div>
              <div style={{ fontSize: 12, color: C.textLight, textAlign: 'right', marginTop: 4 }}>
                <div>{new Date().toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                {organization.groupOwner && (
                  <div style={{ marginTop: 4, fontSize: 11, fontWeight: 600, color: C.navy }}>
                    {organization.groupOwner}
                  </div>
                )}
                {organization.managingGroup && (
                  <div style={{ marginTop: 2, fontSize: 11, color: C.textLight }}>
                    {organization.managingGroup}
                  </div>
                )}
              </div>
            </div>
            <div style={{ height: 4, background: `linear-gradient(90deg, ${C.teal} 0%, ${C.blueMid} 100%)`, margin: '12px 0 0 0', borderRadius: 2 }} />
          </div>
        </Section>

        {/* Campaigns strip */}
        {campaigns.length > 0 && (
          <Section style={{ padding: '14px 28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: C.textLight, textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>
                {campaigns.length} Campaign{campaigns.length !== 1 ? 's' : ''}:
              </div>
              {campaigns.map((c, i) => (
                <div key={i} style={{
                  fontSize: 11, fontWeight: 600, color: c.status === 'completed' ? C.green : C.teal,
                  background: c.status === 'completed' ? C.greenLight : C.tealLight,
                  border: `1px solid ${c.status === 'completed' ? '#86efac' : '#a7d8e6'}`,
                  borderRadius: 20, padding: '3px 10px',
                }}>
                  {c.name}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Overview KPIs */}
        <Section>
          <SectionBand
            label="OVERVIEW"
            color={C.teal}
            stats={[
              { val: pct(overview.completionRate || 0, 0), lbl: 'Completion Rate', color: C.blue },
              { val: pct(overview.averageScore || 0, 0), lbl: 'Average Score', color: C.navy },
              { val: num(overview.uniqueParticipants || 0), lbl: 'Participants', color: C.teal },
            ]}
          />
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <KpiCard value={overview.completionRateDisplay || pct(overview.completionRate || 0, 0)} label="Completion Rate" color={C.blue} />
            <KpiCard value={overview.averageScoreDisplay || pct(overview.averageScore || 0, 0)} label="Average Score" color={C.teal} />
            <KpiCard value={num(overview.uniqueParticipants || 0)} label="Employees Registered" color={C.navy} />
            <KpiCard value={overview.averageTimeSpentDisplay || '0 min'} label="Avg. Time to Complete" color={C.navy} />
            <KpiCard value={num(leaderboard?.totalQualified || 0)} label="Leaderboard Qualifiers" color={C.green} />
            <KpiCard value={overview.targetDelivery?.deliveryRate || '0%'} label="Quiz Delivery Rate" color={C.green} />
          </div>

          {/* Donut completion visual */}
          <DonutChart
            completionRate={overview.completionRate || 0}
            completed={overview.completed || 0}
            notCompleted={Math.max(0, (overview.uniqueParticipants || 0) - (overview.completed || 0))}
            total={overview.uniqueParticipants || 0}
          />

          {/* Benchmark pills strip */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
            <div style={{ background: '#FDE8DC', color: '#C2410C', fontSize: 11, fontWeight: 600, padding: '4px 14px', borderRadius: 20, whiteSpace: 'nowrap' }}>
              Required CR: {pct(benchmark, 0)}
            </div>
            <div style={{ background: '#E5E7EB', color: C.textMid, fontSize: 11, fontWeight: 600, padding: '4px 14px', borderRadius: 20, whiteSpace: 'nowrap' }}>
              Network avg CR: {pct(networkAvg.completionRate || 0, 0)}
            </div>
            <div style={{ background: C.navy, color: C.white, fontSize: 11, fontWeight: 600, padding: '4px 14px', borderRadius: 20, whiteSpace: 'nowrap' }}>
              {organization.name}: {pct(overview.completionRate || 0, 0)}
            </div>
          </div>

          {/* Network average comparison row */}
          {(networkAvg.completionRate > 0 || networkAvg.averageScore > 0) && (
            <>
              <div style={{ height: 1, background: C.border, margin: '20px 0 16px' }} />
              <div style={{ fontSize: 10, fontWeight: 600, color: C.textLight, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
                Network Average — {networkAvg.orgCount ? `${networkAvg.orgCount} organisations` : 'organisations'} under the same group owner
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <KpiCard value={pct(networkAvg.completionRate || 0, 1)} label="Network Completion Rate" color={C.orange} />
                <KpiCard value={pct(networkAvg.averageScore || 0, 1)} label="Network Avg Score" color={C.orange} />
                {/* Delta vs network */}
                <div style={{
                  flex: 1, background: C.white, border: `1px solid ${C.border}`, borderRadius: 6,
                  padding: '14px 20px', textAlign: 'center', minWidth: 110,
                }}>
                  {(() => {
                    const delta = roundTo((overview.completionRate || 0) - (networkAvg.completionRate || 0), 1);
                    const color = delta >= 0 ? C.green : C.red;
                    return <>
                      <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1.1 }}>
                        {delta >= 0 ? '+' : ''}{delta.toFixed(1)}%
                      </div>
                      <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>vs Network (Completion)</div>
                    </>;
                  })()}
                </div>
                <div style={{
                  flex: 1, background: C.white, border: `1px solid ${C.border}`, borderRadius: 6,
                  padding: '14px 20px', textAlign: 'center', minWidth: 110,
                }}>
                  {(() => {
                    const delta = roundTo((overview.averageScore || 0) - (networkAvg.averageScore || 0), 1);
                    const color = delta >= 0 ? C.green : C.red;
                    return <>
                      <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1.1 }}>
                        {delta >= 0 ? '+' : ''}{delta.toFixed(1)}%
                      </div>
                      <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>vs Network (Score)</div>
                    </>;
                  })()}
                </div>
              </div>
            </>
          )}
        </Section>

        {/* Leaderboard */}
        <LeaderboardSection leaderboard={leaderboard} benchmark={benchmark} networkAvg={networkAvg} />

        {/* Departments */}
        <DepartmentsSection departments={departments} benchmark={benchmark} />

        {/* Score distribution */}
        <ScoreDistributionSection scoreBuckets={scoreBuckets} />

        {/* Campaign breakdown */}
        <CampaignBreakdownSection campaignBreakdown={campaignBreakdown} benchmark={benchmark} />

        {/* At-risk non-starters */}
        <AtRiskSection nonStarters={nonStarters} />

        {/* AI Insights */}
        <BuAiInsightSection
          insight={buAiInsight}
          loading={buAiLoading}
          error={buAiError}
          onGenerate={generateBuInsight}
          onInsightChange={setBuAiInsight}
        />

        {/* Footer */}
        <div style={{
          background: C.teal, color: C.white, borderRadius: 6, padding: '12px 24px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8,
        }}>
          <div style={{ fontSize: 11 }}>Confidential — For internal use only</div>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>TAPLEARNGO</div>
        </div>
      </div>

      {/* Save PDF profile modal */}
      <Modal
        title="Save PDF Profile"
        open={savePdfProfileOpen}
        onOk={handleSavePdfProfile}
        onCancel={() => setSavePdfProfileOpen(false)}
        okText="Save"
        okType="default"
        width={360}
      >
        <Input
          placeholder="Profile name (e.g. MBSA Standard)"
          value={pdfProfileName}
          onChange={e => setPdfProfileName(e.target.value)}
          onPressEnter={handleSavePdfProfile}
        />
        {pdfProfiles.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Existing profiles</div>
            {pdfProfiles.map(p => (
              <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ fontSize: 12 }}>{p.name}</span>
                <span style={{ fontSize: 11, color: C.red, cursor: 'pointer' }} onClick={() => handleDeletePdfProfile(p.name)}>Delete</span>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </>
  );
};

export default BusinessUnitReport;
